import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AdminPermissions, InviteAdminResponse, PlatformRole } from '@eskoolhub/types';
import { useToast } from '../components/Toasts';
import { Modal, Switch } from '../components/ui';
import { api, ApiRequestError } from '../lib/api';

const PERMISSION_ROWS: { key: keyof AdminPermissions; label: string; hint: string }[] = [
  { key: 'tickets', label: 'Support tickets', hint: 'Read and work the ticket queue' },
  { key: 'billing', label: 'Billing access', hint: 'Invoices, plans, marking payments received' },
  { key: 'schoolData', label: 'Read-only school data', hint: 'View schools and their data, no changes' },
];

/** A07 — invite an admin. Support accounts get their permissions ticked here. */
export function InviteAdminModal({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<PlatformRole>('SUPPORT');
  const [permissions, setPermissions] = useState<AdminPermissions>({
    tickets: true,
    billing: false,
    schoolData: true,
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const invite = useMutation({
    mutationFn: () =>
      api.post<InviteAdminResponse>('/api/platform-users/invite', {
        name: name.trim(),
        email: email.trim(),
        role,
        ...(role === 'SUPPORT' ? { permissions } : {}),
      }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['platform-users'] });
      toast.push(`Invite sent to ${result.invite.email}`, 'success');
      onClose();
    },
    onError: (error) => {
      if (error instanceof ApiRequestError) {
        setFieldErrors(error.fields ?? {});
        setFormError(error.fields ? null : error.message);
      } else {
        setFormError('Could not send the invite');
      }
    },
  });

  const noPermissions = role === 'SUPPORT' && !permissions.tickets && !permissions.billing && !permissions.schoolData;

  return (
    <Modal
      title="Invite admin"
      onClose={onClose}
      footer={
        <>
          <button className="btn btn--ghost" onClick={onClose} disabled={invite.isPending}>
            Cancel
          </button>
          <button
            className="btn"
            style={{ marginLeft: 'auto' }}
            disabled={invite.isPending || name.trim().length < 2 || !email.includes('@') || noPermissions}
            onClick={() => invite.mutate()}
          >
            {invite.isPending ? 'Sending…' : 'Send invite'}
          </button>
        </>
      }
    >
      {formError ? <div className="banner banner--error">{formError}</div> : null}

      <label className="field">
        <span>Name</span>
        <input
          value={name}
          autoFocus
          placeholder="Devika Rane"
          onChange={(e) => {
            setName(e.target.value);
            setFieldErrors(({ name: _drop, ...rest }) => rest);
          }}
        />
        {fieldErrors.name ? <span className="field__error">{fieldErrors.name}</span> : null}
      </label>

      <label className="field">
        <span>Email</span>
        <input
          type="email"
          value={email}
          placeholder="devika@eskoolhub.com"
          onChange={(e) => {
            setEmail(e.target.value);
            setFieldErrors(({ email: _drop, ...rest }) => rest);
          }}
        />
        {fieldErrors.email ? <span className="field__error">{fieldErrors.email}</span> : null}
      </label>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
        <span style={{ fontSize: 13, fontWeight: 700 }}>Role</span>
        <div className="segmented">
          <button type="button" aria-pressed={role === 'SUPER_ADMIN'} onClick={() => setRole('SUPER_ADMIN')}>
            Super admin
          </button>
          <button type="button" aria-pressed={role === 'SUPPORT'} onClick={() => setRole('SUPPORT')}>
            Support
          </button>
        </div>
      </div>

      {role === 'SUPPORT' ? (
        <div
          style={{
            background: 'var(--paper)',
            border: '1px solid var(--rule)',
            borderRadius: 16,
            padding: 15,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 700 }}>Support permissions</div>
          {PERMISSION_ROWS.map((row) => (
            <div key={row.key} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: 13.5, fontWeight: 700 }}>{row.label}</span>
                <span className="caption">{row.hint}</span>
              </div>
              <Switch
                checked={permissions[row.key]}
                onChange={(next) => setPermissions((current) => ({ ...current, [row.key]: next }))}
                label={row.label}
              />
            </div>
          ))}
          {noPermissions ? (
            <span className="field__error">Give them at least one permission, or the account can't do anything.</span>
          ) : null}
        </div>
      ) : (
        <div className="banner" style={{ background: 'var(--paper)', color: 'var(--ink-soft)' }}>
          A super admin has every permission, including onboarding schools, billing, and managing other admins.
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 12.5, color: 'var(--ink-soft)' }}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="var(--ink-soft)" style={{ flex: 'none' }}>
          <path d="M12 2.4a9.6 9.6 0 1 0 0 19.2 9.6 9.6 0 0 0 0-19.2zm1.2 14.4h-2.4v-6h2.4zm0-7.8h-2.4V6.6h2.4z" />
        </svg>
        2FA is required for every admin account.
      </div>
    </Modal>
  );
}
